import { useRef, useState } from "react";
import { LogIn, LogOut, Loader2, Utensils, Activity, AlertTriangle, HeartPulse, StickyNote, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";

const inputCls = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";
export const fmtT = (v) => (v ? new Date(v).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");
export const fmtDur = (m) => (m == null ? "—" : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`);

export function ClientStayDialog({ client, mode, pin = "", onClose, onDone }) {
  const arriving = mode === "arrive";
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const submitRef = useRef(null);
  const contacts = client.contacts || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!arriving && !name.trim()) { toast.error("Enter who is picking up."); return; }
    if (!arriving && !sig) { toast.error("Escort signature is required."); return; }
    setBusy(true);
    try {
      const body = arriving ? { dropoff_name: name, note, pin } : { pickup_name: name, signature: sig, note, pin };
      await api.post(`/clients/${client.id}/${arriving ? "arrive" : "depart"}`, body);
      toast.success(`${client.full_name} ${arriving ? "checked in" : "checked out"}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="client-stay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {arriving ? <LogIn className="h-5 w-5 text-emerald-600" /> : <LogOut className="h-5 w-5 text-blue-600" />}
            {arriving ? "Check in" : "Check out"} · {client.full_name}
          </DialogTitle>
          <DialogDescription>{arriving ? "Record the arrival and who brought the client." : "Record who is taking the client home. Their signature is required."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">{arriving ? "Dropped off by (optional)" : "Picked up by"}</span>
            {contacts.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {contacts.map((c, i) => (
                  <button type="button" key={i} onClick={() => setName(c.name)} data-testid={`stay-contact-${i}`}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${name === c.name ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}>
                    {c.name}{c.relationship ? ` · ${c.relationship}` : ""}
                  </button>
                ))}
              </div>
            )}
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Escort's full name" data-testid="stay-escort-name" className={inputCls} />
          </div>
          {!arriving && (
            <div>
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Escort signature</span>
              <SignaturePad onChange={setSig} testid="stay-signature" />
            </div>
          )}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={arriving ? "Arrival note — mood, belongings, medication brought…" : "Departure note (optional)"}
            onFocus={() => setTimeout(() => submitRef.current?.scrollIntoView({ block: "end", behavior: "smooth" }), 350)}
            data-testid="stay-note" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary" />
          <DialogFooter>
            <button type="submit" disabled={busy} data-testid="stay-submit" ref={submitRef}
              className={`h-11 rounded-lg px-5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60 ${arriving ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : arriving ? <LogIn className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
              {arriving ? "Check in" : "Check out & sign"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ENTRY = [
  ["meal", "Meal", Utensils, "Breakfast · Lunch · Snack — what & how much"],
  ["activity", "Activity", Activity, "Exercise, crafts, walk, therapy…"],
  ["vitals", "Vitals", HeartPulse, "BP 120/80 · Temp 98.6 · Pulse 72"],
  ["incident", "Incident", AlertTriangle, "Fall, refusal, behaviour — what happened & action taken"],
  ["note", "Note", StickyNote, "Anything else the family or staff should know"],
];
export const ENTRY_ICON = Object.fromEntries(ENTRY.map(([v, , I]) => [v, I]));

export const VITALS = [
  { k: "bp_sys", label: "BP systolic", unit: "mmHg", ph: "120", pair: "bp" },
  { k: "bp_dia", label: "BP diastolic", unit: "mmHg", ph: "80", pair: "bp" },
  { k: "pulse", label: "Pulse", unit: "bpm", ph: "72" },
  { k: "temp", label: "Temperature", unit: "°F", ph: "98.6", step: "0.1" },
  { k: "spo2", label: "Oxygen (SpO₂)", unit: "%", ph: "97" },
  { k: "glucose", label: "Blood glucose", unit: "mg/dL", ph: "110" },
  { k: "weight", label: "Weight", unit: "lb", ph: "150", step: "0.1" },
  { k: "resp", label: "Respiration", unit: "/min", ph: "16" },
];

export function vitalsSummary(data = {}) {
  const parts = [];
  if (data.bp_sys || data.bp_dia) parts.push(`BP ${data.bp_sys || "?"}/${data.bp_dia || "?"} mmHg`);
  for (const v of VITALS) {
    if (v.pair || !data[v.k]) continue;
    parts.push(`${v.label.replace(" (SpO₂)", "")} ${data[v.k]} ${v.unit}`);
  }
  return parts.join(" · ");
}

export function StayNotesDialog({ client, stay, pin = "", onClose, onDone }) {
  const [type, setType] = useState("meal");
  const [text, setText] = useState("");
  const [vitals, setVitals] = useState({});
  const [entries, setEntries] = useState(stay.entries || []);
  const [busy, setBusy] = useState(false);

  const isVitals = type === "vitals";
  const vitalsFilled = Object.values(vitals).some((v) => String(v || "").trim());
  const add = async (e) => {
    e.preventDefault();
    if (isVitals ? !vitalsFilled : !text.trim()) return;
    setBusy(true);
    try {
      const body = isVitals
        ? { type, text: [vitalsSummary(vitals), text.trim()].filter(Boolean).join(" — "), data: Object.fromEntries(Object.entries(vitals).filter(([, v]) => String(v).trim())), pin }
        : { type, text, pin };
      const { data } = await api.post(`/stays/${stay.id}/entries`, body);
      setEntries([...entries, data]);
      setText("");
      setVitals({});
      onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const meta = ENTRY.find(([v]) => v === type);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg h-[80vh] flex flex-col overflow-hidden" data-testid="stay-notes-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display">Stay notes · {client.full_name}</DialogTitle>
          <DialogDescription>Arrived {fmtT(stay.arrived_at)}{stay.departed_at ? ` · left ${fmtT(stay.departed_at)}` : " · on site"}</DialogDescription>
        </DialogHeader>
        {!stay.departed_at && (
          <form onSubmit={add} className="shrink-0 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap gap-1.5">
              {ENTRY.map(([v, label, Icon]) => (
                <button type="button" key={v} onClick={() => setType(v)} data-testid={`entry-type-${v}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${type === v ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            {isVitals && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="vitals-fields">
                {VITALS.map((v) => (
                  <label key={v.k} className="block">
                    <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">{v.label}</span>
                    <div className="relative">
                      <input type="number" inputMode="decimal" step={v.step || "1"} value={vitals[v.k] ?? ""} placeholder={v.ph} data-testid={`vital-${v.k}`}
                        onChange={(e) => setVitals({ ...vitals, [v.k]: e.target.value })}
                        className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-2.5 pr-12 text-sm outline-none focus:border-primary" />
                      <span className="pointer-events-none absolute right-2 top-2.5 text-[10px] text-slate-400">{v.unit}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={isVitals ? "Observation (optional) — e.g. taken seated, after breakfast" : meta?.[3]} data-testid="entry-text" className={`${inputCls} h-10`} />
              <button type="submit" disabled={busy || (isVitals ? !vitalsFilled : !text.trim())} data-testid="entry-add" className="h-10 rounded-lg bg-slate-900 px-3 text-white inline-flex items-center gap-1 text-sm font-semibold disabled:opacity-50">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          </form>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5" data-testid="entry-list">
          {entries.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No notes yet for this stay.</div>}
          {[...entries].reverse().map((e) => {
            const Icon = ENTRY_ICON[e.type] || StickyNote;
            return (
              <div key={e.id} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 text-sm ${e.type === "incident" ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}>
                <Icon className={`h-4 w-4 mt-0.5 ${e.type === "incident" ? "text-rose-600" : "text-slate-500"}`} />
                <div className="flex-1 min-w-0">
                  {e.type === "vitals" && e.data && Object.keys(e.data).length > 0 ? <VitalsChips data={e.data} note={e.text} /> : <div className="text-slate-900">{e.text}</div>}
                  <div className="text-[11px] text-slate-400 capitalize">{e.type} · {fmtT(e.at)} · {e.by_name}</div></div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function VitalsChips({ data = {}, note = "" }) {
  const chips = [];
  if (data.bp_sys || data.bp_dia) chips.push(["BP", `${data.bp_sys || "?"}/${data.bp_dia || "?"}`, "mmHg"]);
  for (const v of VITALS) if (!v.pair && data[v.k]) chips.push([v.label.replace("Blood glucose", "Glucose").replace("Temperature", "Temp").replace("Oxygen (SpO₂)", "SpO₂").replace("Respiration", "Resp"), data[v.k], v.unit]);
  const obs = note.includes(" — ") ? note.split(" — ").slice(1).join(" — ") : (chips.length ? "" : note);
  return (
    <div data-testid="vitals-chips">
      <div className="flex flex-wrap gap-1.5">
        {chips.map(([l, v, u]) => (
          <span key={l} className="inline-flex items-baseline gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{l}</span>
            <span className="font-display font-bold text-slate-900">{v}</span>
            <span className="text-[10px] text-slate-400">{u}</span>
          </span>
        ))}
      </div>
      {obs && <div className="mt-1 text-xs text-slate-600 italic">{obs}</div>}
    </div>
  );
}
